/**
 * WS-P: why a device has no baked mockup for a version — as ONE sentence per cause.
 *
 * AUD-8 LB-8: four distinct causes collapsed to the same silent outcome, and three of them left
 * no trace on device B at all. The Error log could not answer "why is this blank" without a code
 * read. These strings exist so it can, and so the three call sites (Batch open, auto-sync heal,
 * auto-sync not-synced) cannot drift into three different vocabularies for one problem.
 *
 * This module changes NO control flow. `autoSync`'s "never bulk-download" policy and its
 * "only heal when a bake EXISTS" policy are both correct as written and are preserved verbatim;
 * this makes them *audible*, not different.
 */

export type BakeAbsence =
  | "not-published"   // the cloud version carries no baked-asset refs at all
  | "not-synced"      // this device has never synced the template record
  | "no-version";     // the row has no currentVersion (nothing to key assets by)

/**
 * One sentence per cause, keyed exhaustively.
 *
 * `satisfies Record<BakeAbsence, …>` is the census: adding a `BakeAbsence` member without a
 * message here is a `tsc` error, not a runtime surprise. (Mirrors WS-N's `_censusIsComplete`
 * pattern.) A vocabulary that can silently rot is how four causes became one sentence in the
 * first place.
 */
const MESSAGES = {
  "not-published": (name: string) =>
    `“${name}” has no published mock-up — it was baked on another machine but never uploaded, so ` +
    `this device has nothing to download. Re-open it there and let the mock-up sync finish.`,
  "not-synced": (name: string) =>
    `“${name}” is not synced on this device, so its mock-up was never downloaded. Open Templates ` +
    `and press Sync on it.`,
  "no-version": (name: string) =>
    `“${name}” has no saved version yet, so there is no mock-up to download.`,
} satisfies Record<BakeAbsence, (name: string) => string>;

export function bakeAbsenceMessage(cause: BakeAbsence, templateName: string): string {
  return MESSAGES[cause](templateName);
}

/**
 * Emitted `(versionId, cause)` pairs.
 *
 * `autoSync` runs on a 60 s ticker over every synced template; an unconditional breadcrumb
 * would flood the ring buffer and bury the very signal this adds. Deduped per pair, and
 * cleared by `clearHydration(versionId)` (unsync) and `clearAllHydration()` (sign-out) so a
 * remove → re-sync repair announces itself again.
 *
 * Keyed by versionId, not templateId: a NEW version is a new fact worth re-reporting.
 */
const announced = new Set<string>();

/** Key under which a (version, cause) pair has already been announced. */
function key(versionId: string, cause: BakeAbsence): string {
  return `${versionId}:${cause}`;
}

/**
 * True the FIRST time this (version, cause) pair is seen, false every time after — so a caller
 * reads `if (shouldAnnounceBakeAbsence(...)) logger?.breadcrumb(...)`.
 */
export function shouldAnnounceBakeAbsence(versionId: string, cause: BakeAbsence): boolean {
  const k = key(versionId, cause);
  if (announced.has(k)) return false;
  announced.add(k);
  return true;
}

/** Forget one version's announcements — called from `clearHydration` (unsync / remove). */
export function forgetBakeAbsence(versionId: string): void {
  for (const cause of Object.keys(MESSAGES) as BakeAbsence[]) announced.delete(key(versionId, cause));
}

/** Forget every announcement — called from `clearAllHydration` (sign-out). */
export function forgetAllBakeAbsence(): void {
  announced.clear();
}

/**
 * The one shape every call site logs, so the three of them cannot drift.
 * Emits at most once per `(versionId, cause)`; returns whether it emitted.
 */
export function announceBakeAbsence(
  logger: { breadcrumb(category: string, message: string, data?: Record<string, unknown>): void } | undefined,
  cause: BakeAbsence,
  row: { templateId: string; versionId: string | undefined; templateName: string },
): boolean {
  // A row with no version still needs a stable dedup key, or `no-version` would re-announce
  // every tick — exactly the flood this guard exists to prevent.
  const versionKey = row.versionId ?? `template:${row.templateId}`;
  // review-1 m-2: the logger is checked BEFORE the slot is consumed. `shouldAnnounceBakeAbsence`
  // marks the pair as announced, so consuming it first meant that a first call with no logger
  // (a standalone mount, an early tick before services settle, a test harness) wrote no
  // breadcrumb AND suppressed every later call that had one — the wave's own subject, silenced
  // for that version, by the guard meant to protect it.
  if (!logger) return false;
  if (!shouldAnnounceBakeAbsence(versionKey, cause)) return false;
  logger.breadcrumb("action", bakeAbsenceMessage(cause, row.templateName), {
    templateId: row.templateId,
    versionId: row.versionId,
    cause,
  });
  return true;
}
